import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import type { ComponentType, ReactNode } from "react";

import { SidebarMenu, SidebarMenuItem, useSidebar } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import lawWorksBlueIcon from "@/assets/LawWorksBlueNoText.png";
import type { SidebarDestination } from "./useSidebarPageCache";

type SidebarSection = {
  title: string;
  label?: string;
  icon: ComponentType<{ className?: string }>;
  submenu: ReactNode;
  destination?: SidebarDestination;
};

export function SidebarSectionRail({
  items,
  selectedSection,
  onSelect,
  hasPanel,
}: {
  items: SidebarSection[];
  selectedSection?: string;
  onSelect: (section: string, destination?: SidebarDestination) => boolean;
  hasPanel: boolean;
}) {
  const { state, isMobile, setOpen, setOpenMobile, toggleSidebar } =
    useSidebar();
  const isCollapsed = !isMobile && state === "collapsed";
  const showToggle = hasPanel || isMobile;

  return (
    <div className="border-sidebar-border flex w-20 shrink-0 flex-col border-r">
      <div className="flex h-14 shrink-0 items-center justify-center">
        <img
          src={lawWorksBlueIcon}
          alt="LawWorks"
          className="size-7 object-contain"
          width={28}
          height={28}
          decoding="async"
        />
      </div>
      <nav
        aria-label="Main sections"
        className="min-h-0 flex-1 overflow-y-auto px-1 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <SidebarMenu className="gap-1">
          {items.map((item) => {
            const label = item.label ?? item.title;
            const selected = selectedSection === item.title;
            const className = cn(
              "flex min-h-12 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-lg px-1.5 py-1.5 text-center text-[11px] leading-[14px] font-medium outline-hidden transition-none focus-visible:ring-2 focus-visible:ring-neutral-500",
              selected
                ? "bg-neutral-900 text-white hover:bg-neutral-900 dark:hover:bg-neutral-900"
                : "hover:bg-neutral-200 dark:hover:bg-white/10",
            );
            const content = (
              <>
                <item.icon className="size-[18px] shrink-0" />
                <span>{label}</span>
              </>
            );

            return (
              <SidebarMenuItem key={item.title}>
                {!item.destination ? (
                  <button
                    type="button"
                    className={className}
                    aria-label={label}
                    aria-pressed={selected}
                    aria-controls="sidebar-section-panel"
                    title={label}
                    onClick={() => {
                      onSelect(item.title);
                      if (isCollapsed) setOpen(true);
                    }}
                  >
                    {content}
                  </button>
                ) : (
                  <Link
                    to={item.destination.to}
                    search={item.destination.search}
                    hash={item.destination.hash}
                    className={className}
                    aria-label={label}
                    title={label}
                    onClick={(event) => {
                      if (
                        event.button !== 0 ||
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey
                      )
                        return;
                      if (onSelect(item.title, item.destination)) {
                        event.preventDefault();
                      }
                      if (item.submenu && isCollapsed) setOpen(true);
                      setOpenMobile(false);
                    }}
                  >
                    {content}
                  </Link>
                )}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </nav>
      <div className="h-12 shrink-0 px-2 py-2">
        {showToggle ? (
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={
              isMobile
                ? "Close navigation"
                : isCollapsed
                  ? "Expand sidebar"
                  : "Collapse sidebar"
            }
            title={
              isMobile
                ? "Close navigation"
                : isCollapsed
                  ? "Expand sidebar"
                  : "Collapse sidebar"
            }
            className="text-muted-foreground hover:text-foreground flex h-8 w-full cursor-pointer items-center justify-center rounded-md outline-hidden hover:bg-neutral-200 focus-visible:ring-2 focus-visible:ring-neutral-500 dark:hover:bg-white/10"
          >
            {isCollapsed ? (
              <ChevronRight className="size-4" />
            ) : (
              <ChevronLeft className="size-4" />
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}
